import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  ClipboardList, CheckCircle2, Clock, User, Calendar,
  MapPin, Camera, Play, Check, Settings, LogOut, Home, MessageSquare, Phone, Mail, Save,
  Shield, Upload, FileText, AlertTriangle, AlertCircle, Eye, Star, DollarSign, Briefcase, Send, Loader2, Globe,
  X, ZoomIn, ChevronDown, ChevronUp, ArrowLeft, Image, Building2, Award, ImagePlus, Trash2,
  BadgeCheck, Download, BarChart3, TrendingUp, Percent, Search, HelpCircle
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';
import { providerAPI, messagesAPI, serviceRequestAPI, serviceMarketplaceAPI } from '../services/api';
import FAQFeedback from '../components/FAQFeedback';
import MessagingPanel from '../components/MessagingPanel';
import LivePhotoCapture from '../components/LivePhotoCapture';
import RoleSwitcher from '../components/RoleSwitcher';
import ReferralDashboard from '../components/ReferralDashboard';
import { Sparkles } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const ProviderDashboard = () => {
  const navigate = useNavigate();
  const { user, logout, getAuthToken, isAuthenticated, loading: authLoading } = useAuth();
  const { toast } = useToast();
  
  const [activeTab, setActiveTab] = useState('assigned');
  const [dashboard, setDashboard] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [availability, setAvailability] = useState([]);
  const [selectedTask, setSelectedTask] = useState(null);
  const [completionNotes, setCompletionNotes] = useState('');
  // Live camera + expanded task state
  const [showCamera, setShowCamera] = useState(false);
  const [cameraTaskId, setCameraTaskId] = useState(null);
  const [sendingPhoto, setSendingPhoto] = useState(false);
  const [expandedTaskId, setExpandedTaskId] = useState(null);
  const [fullscreenPhoto, setFullscreenPhoto] = useState(null);
  // Profile editing state
  const [profileForm, setProfileForm] = useState({ name: '', email: '', phone: '', bio: '' });
  const [profileSaving, setProfileSaving] = useState(false);
  // Messaging state
  const [conversations, setConversations] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [selectedConversationId, setSelectedConversationId] = useState(null);
  // Compliance state
  const [docUploading, setDocUploading] = useState(null);
  // Marketplace state
  const [quoteRequests, setQuoteRequests] = useState([]);
  const [quotesLoading, setQuotesLoading] = useState(false);
  const [quoteForm, setQuoteForm] = useState({});

  // Unread messages badge + toast notification
  const [unreadCount, setUnreadCount] = useState(0);
  const prevUnreadRef = useRef(0);
  // Acknowledged state so the red badge clears the moment the user opens
  // the Messages tab, only reappearing on a genuinely new message.
  const [messagesAck, setMessagesAck] = useState(false);

  // Job Board state
  const [marketplaceJobs, setMarketplaceJobs] = useState([]);
  const [marketplaceLoading, setMarketplaceLoading] = useState(false);
  const [marketplaceFilter, setMarketplaceFilter] = useState('all');
  const [submittingQuote, setSubmittingQuote] = useState(null);
  const [earnings, setEarnings] = useState(null);
  const [earningsLoading, setEarningsLoading] = useState(false);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [selectedServiceTypes, setSelectedServiceTypes] = useState([]);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  // Enhanced profile fields
  const [marketplaceProfile, setMarketplaceProfile] = useState({
    businessName: '', tagline: '', yearsExperience: '', serviceArea: '', publicProfile: false, hourlyRate: '', customQuotesOnly: false, qualifications: []
  });
  // Profile photo, portfolio, company & tax
  const [profilePhotoUrl, setProfilePhotoUrl] = useState(null);
  const [uploadingProfilePhoto, setUploadingProfilePhoto] = useState(false);
  const [portfolioPhotos, setPortfolioPhotos] = useState([]);
  const [uploadingPortfolio, setUploadingPortfolio] = useState(false);
  const [showPortfolioCamera, setShowPortfolioCamera] = useState(false);
  const [deletingPortfolioPhoto, setDeletingPortfolioPhoto] = useState(null);
  const [newQualification, setNewQualification] = useState('');
  const [companyDetails, setCompanyDetails] = useState({
    tradingName: '', companyName: '', companyNumber: '', registeredAddress: '', isSoleTrader: true
  });
  const [taxDetails, setTaxDetails] = useState({
    hasVatNumber: false, vatNumber: '', utrNumber: ''
  });

  useEffect(() => {
    if (authLoading) return; // Wait for auth to finish loading
    if (!isAuthenticated()) {
      navigate('/login', { replace: true });
      return;
    }

    if (!user?.roles?.includes('service_provider')) {
      toast({
        title: 'Access Denied',
        description: 'You must be a registered service provider to access this page',
        variant: 'destructive'
      });
      navigate('/', { replace: true });
      return;
    }

    fetchDashboard();
  }, [authLoading, isAuthenticated, user, navigate]);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const token = getAuthToken();
      if (token) {
        const data = await messagesAPI.getUnreadCount(token);
        setUnreadCount(data.providerUnread || 0);
      }
    } catch {}
  }, [getAuthToken]);

  const handleUnreadChange = useCallback(async () => {
    try {
      const token = getAuthToken();
      if (token) {
        const data = await messagesAPI.getUnreadCount(token);
        const newCount = data.providerUnread || 0;
        if (newCount > prevUnreadRef.current && activeTab !== 'messages') {
          toast({ title: '💬 New message', description: 'You have a new message.' });
          setMessagesAck(false);
        }
        prevUnreadRef.current = newCount;
        setUnreadCount(newCount);
      }
    } catch {}
  }, [getAuthToken, activeTab, toast]);

  useEffect(() => {
    if (activeTab === 'messages') {
      setMessagesAck(true);
      setUnreadCount(0);
      prevUnreadRef.current = 0;
    }
  }, [activeTab]);

  useEffect(() => {
    if (!authLoading && isAuthenticated() && user?.roles?.includes('service_provider')) {
      fetchUnreadCount();
      const interval = setInterval(fetchUnreadCount, 30000);
      return () => clearInterval(interval);
    }
  }, [authLoading, isAuthenticated, user, fetchUnreadCount]);

  const fetchDashboard = async () => {
    try {
      const token = getAuthToken();
      const dashboardData = await providerAPI.getDashboard(token);
      setDashboard(dashboardData);

      // Populate profile form
      setProfileForm({
        name: dashboardData?.profile?.name || user?.name || '',
        email: dashboardData?.profile?.email || user?.email || '',
        phone: dashboardData?.profile?.phone || '',
        bio: dashboardData?.profile?.bio || '',
      });

      const tasksData = await providerAPI.getTasks(token);
      setTasks(tasksData);

      const availData = await providerAPI.getAvailability(token);
      setAvailability(availData);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
      toast({ title: 'Error', description: 'Failed to load dashboard', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    setProfileSaving(true);
    try {
      const token = getAuthToken();
      await providerAPI.updateProfile(profileForm, token);
      toast({ title: 'Success', description: 'Profile updated successfully' });
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to update profile', variant: 'destructive' });
    } finally {
      setProfileSaving(false);
    }
  };

  const fetchConversations = async () => {
    setMessagesLoading(true);
    try {
      const token = getAuthToken();
      const data = await messagesAPI.getConversations(token);
      setConversations(data || []);
    } catch (error) {
      console.error('Error fetching conversations:', error);
    } finally {
      setMessagesLoading(false);
    }
  };

  const handleAcceptTask = async (taskId) => {
    try {
      const token = getAuthToken();
      await providerAPI.acceptTask(taskId, token);
      toast({ title: 'Success', description: 'Task accepted' });
      fetchDashboard();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to accept task', variant: 'destructive' });
    }
  };

  const handleStartTask = async (taskId) => {
    try {
      const token = getAuthToken();
      await providerAPI.startTask(taskId, token);
      toast({ title: 'Success', description: 'Task started' });
      fetchDashboard();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to start task', variant: 'destructive' });
    }
  };

  const handleCompleteTask = async (taskId) => {
    try {
      const token = getAuthToken();
      await providerAPI.completeTask(taskId, { notes: completionNotes }, token);
      toast({ title: 'Success', description: 'Task completed!' });
      setSelectedTask(null);
      setCompletionNotes('');
      fetchDashboard();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to complete task', variant: 'destructive' });
    }
  };

  const handleOpenCamera = (taskId) => {
    setCameraTaskId(taskId);
    setShowCamera(true);
  };

  const handleCapturePhoto = async (blob, metadata) => {
    if (!cameraTaskId) return;
    setShowCamera(false);
    setSendingPhoto(true);
    try {
      const token = getAuthToken();
      await providerAPI.uploadEvidencePhoto(cameraTaskId, blob, metadata, 'evidence', token);
      toast({ title: 'Photo Saved', description: 'Evidence photo captured and stored' });
      fetchDashboard();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to save evidence photo', variant: 'destructive' });
    } finally {
      setSendingPhoto(false);
      setCameraTaskId(null);
    }
  };

  const handleDocumentUpload = async (docType, file) => {
    setDocUploading(docType);
    try {
      const token = getAuthToken();
      await providerAPI.uploadDocument(docType, file, token);
      toast({ title: 'Document Uploaded', description: 'Your document has been submitted for admin verification' });
      fetchDashboard();
    } catch (error) {
      toast({ title: 'Upload Failed', description: error.response?.data?.detail || 'Failed to upload document', variant: 'destructive' });
    } finally {
      setDocUploading(null);
    }
  };

  // Job Board marketplace fetch
  const fetchMarketplaceJobs = async (category = null) => {
    setMarketplaceLoading(true);
    try {
      const token = getAuthToken();
      const params = { limit: 50 };
      if (category && category !== 'all') params.category = category;
      const data = await providerAPI.getMarketplaceJobs(token, params);
      setMarketplaceJobs(data.jobs || []);
    } catch (error) {
      console.error('Error fetching marketplace jobs:', error);
    } finally {
      setMarketplaceLoading(false);
    }
  };

  const handleBidOnJob = async (job) => {
    try {
      const token = getAuthToken();
      const conv = await messagesAPI.createConversation({
        participantId: job.hostId,
        conversationType: 'task',
        participantType: 'provider',
        relatedType: 'task',
        relatedId: job.taskId,
        initialMessage: `Hi, I'm interested in your task: "${job.title}". I'd like to discuss the details and provide a quote.`
      }, token);
      setSelectedConversationId(conv._id);
      setActiveTab('messages');
      toast({ title: 'Message Sent', description: `You've expressed interest in "${job.title}". The host will see your message.` });
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to send message', variant: 'destructive' });
    }
  };

  const fetchQuoteRequests = async () => {
    setQuotesLoading(true);
    try {
      const token = getAuthToken();
      const data = await serviceRequestAPI.getAvailable(null, token);
      setQuoteRequests(data || []);
    } catch (error) {
      console.error('Error fetching quote requests:', error);
    } finally {
      setQuotesLoading(false);
    }
  };

  const handleSubmitQuote = async (requestId) => {
    const form = quoteForm[requestId];
    if (!form?.amount || form.amount <= 0) {
      toast({ title: 'Error', description: 'Please enter a valid quote amount', variant: 'destructive' });
      return;
    }
    setSubmittingQuote(requestId);
    try {
      const token = getAuthToken();
      await serviceRequestAPI.submitQuote(requestId, {
        amount: parseFloat(form.amount),
        description: form.description || '',
        estimatedDuration: form.estimatedDuration || '',
        availableDate: form.availableDate || null,
      }, token);
      toast({ title: 'Quote Submitted', description: 'Your quote has been sent to the client' });
      setQuoteForm(prev => ({ ...prev, [requestId]: null }));
      fetchQuoteRequests();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to submit quote', variant: 'destructive' });
    } finally {
      setSubmittingQuote(null);
    }
  };

  const fetchEarnings = async () => {
    setEarningsLoading(true);
    try {
      const token = getAuthToken();
      const data = await providerAPI.getEarnings(token);
      setEarnings(data);
    } catch (error) {
      console.error('Error fetching earnings:', error);
    } finally {
      setEarningsLoading(false);
    }
  };

  const fetchAnalytics = async () => {
    setAnalyticsLoading(true);
    try {
      const token = getAuthToken();
      const data = await providerAPI.getAnalytics(token);
      setAnalyticsData(data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setAnalyticsLoading(false);
    }
  };

  // Load categories for profile
  useEffect(() => {
    serviceMarketplaceAPI.getCategories().then(setCategories).catch(console.error);
  }, []);

  // Initialize marketplace profile fields from dashboard data
  useEffect(() => {
    if (dashboard?.profile) {
      const p = dashboard.profile;
      setMarketplaceProfile({
        businessName: p.businessName || '',
        tagline: p.tagline || '',
        yearsExperience: p.yearsExperience || '',
        serviceArea: (p.serviceArea?.postcodes || []).join(', '),
        publicProfile: p.publicProfile || false,
        hourlyRate: p.pricing?.hourlyRate || p.hourlyRate || '',
        customQuotesOnly: p.pricing?.customQuotesOnly || false,
        qualifications: p.qualifications || [],
      });
      setProfilePhotoUrl(p.profilePhoto || null);
      setPortfolioPhotos(p.portfolioPhotos || []);
      setCompanyDetails(p.companyDetails || { tradingName: '', companyName: '', companyNumber: '', registeredAddress: '', isSoleTrader: true });
      setTaxDetails(p.taxDetails || { hasVatNumber: false, vatNumber: '', utrNumber: '' });
      setSelectedServiceTypes(p.serviceTypes || []);
    }
  }, [dashboard]);

  const handleSaveMarketplaceProfile = async () => {
    setProfileSaving(true);
    try {
      const token = getAuthToken();
      const postcodes = marketplaceProfile.serviceArea.split(',').map(s => s.trim()).filter(Boolean);
      await providerAPI.updateProfile({
        ...profileForm,
        businessName: marketplaceProfile.businessName,
        tagline: marketplaceProfile.tagline,
        yearsExperience: marketplaceProfile.yearsExperience ? parseInt(marketplaceProfile.yearsExperience) : 0,
        serviceArea: { postcodes },
        publicProfile: marketplaceProfile.publicProfile,
        qualifications: marketplaceProfile.qualifications,
        serviceTypes: selectedServiceTypes,
        pricing: {
          hourlyRate: marketplaceProfile.hourlyRate ? parseFloat(marketplaceProfile.hourlyRate) : null,
          customQuotesOnly: marketplaceProfile.customQuotesOnly,
        },
      }, token);
      toast({ title: 'Success', description: 'Profile updated successfully' });
      fetchDashboard();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to update profile', variant: 'destructive' });
    } finally {
      setProfileSaving(false);
    }
  };

  const handleProfilePhotoUpload = async (file) => {
    setUploadingProfilePhoto(true);
    try {
      const token = getAuthToken();
      const result = await providerAPI.uploadProfilePhoto(file, token);
      setProfilePhotoUrl(result.url);
      toast({ title: 'Success', description: 'Profile photo updated' });
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to upload photo', variant: 'destructive' });
    } finally {
      setUploadingProfilePhoto(false);
    }
  };

  const handlePortfolioUpload = async (file) => {
    setUploadingPortfolio(true);
    try {
      const token = getAuthToken();
      const result = await providerAPI.uploadPortfolioPhoto(file, token);
      setPortfolioPhotos(prev => [...prev, result.url]);
      toast({ title: 'Success', description: 'Portfolio photo added' });
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to upload photo', variant: 'destructive' });
    } finally {
      setUploadingPortfolio(false);
    }
  };

  const handlePortfolioCapture = async (blob, metadata) => {
    setShowPortfolioCamera(false);
    const file = new File([blob], `portfolio_${Date.now()}.jpg`, { type: 'image/jpeg' });
    await handlePortfolioUpload(file);
  };

  const handleDeletePortfolioPhoto = async (url) => {
    const filename = url.split('/').pop();
    setDeletingPortfolioPhoto(filename);
    try {
      const token = getAuthToken();
      await providerAPI.deletePortfolioPhoto(filename, token);
      setPortfolioPhotos(prev => prev.filter(p => p !== url));
      toast({ title: 'Deleted', description: 'Portfolio photo removed' });
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to delete photo', variant: 'destructive' });
    } finally {
      setDeletingPortfolioPhoto(null);
    }
  };

  const handleSaveCompanyAndTax = async () => {
    setProfileSaving(true);
    try {
      const token = getAuthToken();
      await providerAPI.updateProfile({ companyDetails, taxDetails }, token);
      toast({ title: 'Success', description: 'Company & tax details saved' });
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to save details', variant: 'destructive' });
    } finally {
      setProfileSaving(false);
    }
  };

  const addQualification = () => {
    const q = newQualification.trim();
    if (q && !marketplaceProfile.qualifications.includes(q)) {
      setMarketplaceProfile(prev => ({ ...prev, qualifications: [...prev.qualifications, q] }));
      setNewQualification('');
    }
  };

  const removeQualification = (q) => {
    setMarketplaceProfile(prev => ({ ...prev, qualifications: prev.qualifications.filter(x => x !== q) }));
  };

  const handleMessageHost = async (task) => {
    try {
      const token = getAuthToken();
      const conv = await messagesAPI.createConversation({
        participantId: task.hostId,
        conversationType: 'task',
        participantType: 'provider',
        relatedType: 'task',
        relatedId: task._id,
        initialMessage: `Hi, regarding the ${task.taskType || 'assigned'} task at ${task.propertyTitle || 'the property'}...`
      }, token);
      setSelectedConversationId(conv._id);
      setActiveTab('messages');
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to start conversation', variant: 'destructive' });
    }
  };

  const toggleExpandTask = (taskId) => {
    setExpandedTaskId(prev => prev === taskId ? null : taskId);
  };

  const assignedTasks = tasks.filter(t => t.status === 'pending' || t.status === 'accepted');
  const inProgressTasks = tasks.filter(t => t.status === 'in_progress');
  const completedTasks = tasks.filter(t => t.status === 'completed');

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-amber-100 text-amber-800';
      case 'accepted': return 'bg-blue-100 text-blue-800';
      case 'in_progress': return 'bg-purple-100 text-purple-800';
      case 'completed': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getServiceTypeIcon = (type) => {
    switch (type) {
      case 'cleaning': return '🧹';
      case 'linen': return '🛏️';
      case 'handyman': return '🔧';
      case 'maintenance': return '🔨';
      case 'inspection': return '🔍';
      default: return '📋';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F4F2E9] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1F3A68] mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F4F2E9]">
      {/* Header */}
      <header className="bg-white shadow-sm border-b sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <div className="h-[30px] w-auto flex items-center justify-center flex-shrink-0"><img src="/logo.png" alt="Ariv" className="h-[30px] w-auto object-contain" /></div>
              <span className="text-lg font-semibold text-[#1F3A68]">Provider Portal</span>
            </div>
            <div className="flex items-center gap-2 sm:gap-4">
              <span className="hidden sm:inline text-gray-600">{user?.lastLoginAt ? 'Welcome back' : 'Welcome'}, {user?.name?.split(' ')[0]}</span>
              <RoleSwitcher currentPortal="provider" />
              <Button variant="outline" size="sm" onClick={() => { logout(); navigate('/'); }}>
                <LogOut size={16} className="mr-1" /> <span className="hidden sm:inline">Logout</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Stats Cards */}
        <div className="grid md:grid-cols-4 gap-6 mb-8">
          <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white">
            <CardContent className="p-6">
              <div className="flex items-center gap-3">
                <Clock size={32} />
                <div>
                  <div className="text-3xl font-bold">{dashboard?.stats?.pendingTasks || 0}</div>
                  <div className="text-amber-100">Pending Tasks</div>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
            <CardContent className="p-6">
              <div className="flex items-center gap-3">
                <Play size={32} />
                <div>
                  <div className="text-3xl font-bold">{dashboard?.stats?.inProgressTasks || 0}</div>
                  <div className="text-purple-100">In Progress</div>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
            <CardContent className="p-6">
              <div className="flex items-center gap-3">
                <CheckCircle2 size={32} />
                <div>
                  <div className="text-3xl font-bold">{dashboard?.stats?.completedTasks || 0}</div>
                  <div className="text-green-100">Completed</div>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-[#1F3A68] to-[#2d4f8a] text-white">
            <CardContent className="p-6">
              <div className="flex items-center gap-3">
                <User size={32} />
                <div>
                  <div className="text-3xl font-bold">{dashboard?.stats?.rating?.toFixed(1) || '0.0'}</div>
                  <div className="text-blue-100">Rating</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="flex flex-wrap justify-center w-full bg-white px-3 sm:px-4 py-2 rounded-xl shadow-md gap-1.5 mb-6">
            <TabsTrigger value="assigned" className="px-3 py-1.5 text-xs sm:text-sm data-[state=active]:bg-[#1F3A68] data-[state=active]:text-white rounded-lg whitespace-normal text-center leading-tight max-w-[110px] sm:max-w-none" data-testid="provider-assigned-tab">
              <ClipboardList size={14} className="mr-1" />
              Assigned ({assignedTasks.length})
            </TabsTrigger>
            <TabsTrigger value="inprogress" className="px-3 py-1.5 text-xs sm:text-sm data-[state=active]:bg-[#1F3A68] data-[state=active]:text-white rounded-lg whitespace-normal text-center leading-tight max-w-[110px] sm:max-w-none" data-testid="provider-inprogress-tab">
              <Play size={14} className="mr-1" />
              In Progress ({inProgressTasks.length})
            </TabsTrigger>
            <TabsTrigger value="completed" className="px-3 py-1.5 text-xs sm:text-sm data-[state=active]:bg-[#1F3A68] data-[state=active]:text-white rounded-lg whitespace-normal text-center leading-tight max-w-[110px] sm:max-w-none" data-testid="provider-completed-tab">
              <CheckCircle2 size={14} className="mr-1" />
              Completed ({completedTasks.length})
            </TabsTrigger>
            <TabsTrigger value="availability" className="px-3 py-1.5 text-xs sm:text-sm data-[state=active]:bg-[#1F3A68] data-[state=active]:text-white rounded-lg whitespace-normal text-center leading-tight max-w-[110px] sm:max-w-none" data-testid="provider-availability-tab">
              <Calendar size={14} className="mr-1" />
              Availability
            </TabsTrigger>
            <TabsTrigger value="quotes" className="px-3 py-1.5 text-xs sm:text-sm data-[state=active]:bg-[#1F3A68] data-[state=active]:text-white rounded-lg whitespace-normal text-center leading-tight max-w-[110px] sm:max-w-none" data-testid="provider-quotes-tab" onClick={() => fetchQuoteRequests()}>
              <FileText size={14} className="mr-1" />
              Quotes
            </TabsTrigger>
            <TabsTrigger value="jobboard" className="px-3 py-1.5 text-xs sm:text-sm data-[state=active]:bg-[#1F3A68] data-[state=active]:text-white rounded-lg whitespace-normal text-center leading-tight max-w-[110px] sm:max-w-none" data-testid="provider-jobboard-tab" onClick={() => fetchMarketplaceJobs()}>
              <Search size={14} className="mr-1" />
              Job Board
            </TabsTrigger>
            <TabsTrigger value="earnings" className="px-3 py-1.5 text-xs sm:text-sm data-[state=active]:bg-[#1F3A68] data-[state=active]:text-white rounded-lg whitespace-normal text-center leading-tight max-w-[110px] sm:max-w-none" data-testid="provider-earnings-tab" onClick={() => fetchEarnings()}>
              <DollarSign size={14} className="mr-1" />
              Earnings
            </TabsTrigger>
            <TabsTrigger value="analytics" className="px-3 py-1.5 text-xs sm:text-sm data-[state=active]:bg-[#1F3A68] data-[state=active]:text-white rounded-lg whitespace-normal text-center leading-tight max-w-[110px] sm:max-w-none" data-testid="provider-analytics-tab" onClick={() => fetchAnalytics()}>
              <BarChart3 size={14} className="mr-1" />
              Analytics
            </TabsTrigger>
            <TabsTrigger value="messages" className="relative px-3 py-1.5 text-xs sm:text-sm data-[state=active]:bg-[#1F3A68] data-[state=active]:text-white rounded-lg" data-testid="provider-messages-tab" onClick={() => fetchConversations()}>
              <MessageSquare size={14} className="mr-1" />
              Messages
              {unreadCount > 0 && !messagesAck && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#CB3B3B] text-white text-xs rounded-full flex items-center justify-center">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="compliance" className="px-3 py-1.5 text-xs sm:text-sm data-[state=active]:bg-[#1F3A68] data-[state=active]:text-white rounded-lg whitespace-normal text-center leading-tight max-w-[110px] sm:max-w-none" data-testid="provider-compliance-tab">
              <Shield size={14} className="mr-1" />
              Compliance
            </TabsTrigger>
            <TabsTrigger value="profile" className="px-3 py-1.5 text-xs sm:text-sm data-[state=active]:bg-[#1F3A68] data-[state=active]:text-white rounded-lg whitespace-normal text-center leading-tight max-w-[110px] sm:max-w-none" data-testid="provider-profile-tab">
              <Settings size={14} className="mr-1" />
              Profile
            </TabsTrigger>
            <TabsTrigger value="referrals" className="px-3 py-1.5 text-xs sm:text-sm data-[state=active]:bg-[#1F3A68] data-[state=active]:text-white rounded-lg whitespace-normal text-center leading-tight max-w-[110px] sm:max-w-none" data-testid="provider-referrals-tab">
              <Sparkles size={14} className="mr-1" />
              Refer &amp; Earn
            </TabsTrigger>
            <TabsTrigger value="faq" className="px-3 py-1.5 text-xs sm:text-sm data-[state=active]:bg-[#1F3A68] data-[state=active]:text-white rounded-lg whitespace-normal text-center leading-tight max-w-[110px] sm:max-w-none" data-testid="provider-faq-tab">
              <HelpCircle size={14} className="mr-1" />
              FAQ
            </TabsTrigger>
          </TabsList>

          {/* Assigned Jobs Tab */}
          <TabsContent value="assigned">
            {assignedTasks.length === 0 ? (
              <Card className="p-8 text-center">
                <ClipboardList size={48} className="mx-auto text-gray-400 mb-4" />
                <h3 className="text-xl font-semibold text-gray-700">No assigned tasks</h3>
                <p className="text-gray-500">New tasks will appear here when assigned by hosts</p>
              </Card>
            ) : (
              <div className="space-y-4">
                {assignedTasks.map(task => (
                  <TaskCard
                    key={task._id}
                    task={task}
                    expanded={expandedTaskId === task._id}
                    onToggleExpand={() => toggleExpandTask(task._id)}
                    onAccept={() => handleAcceptTask(task._id)}
                    onStart={() => handleStartTask(task._id)}
                    onMessageHost={() => handleMessageHost(task)}
                    getStatusColor={getStatusColor}
                    getServiceTypeIcon={getServiceTypeIcon}
                  />
                ))}
              </div>
            )}
          </TabsContent>

          {/* In Progress Tab */}
          <TabsContent value="inprogress">
            {inProgressTasks.length === 0 ? (
              <Card className="p-8 text-center">
                <Play size={48} className="mx-auto text-gray-400 mb-4" />
                <h3 className="text-xl font-semibold text-gray-700">No tasks in progress</h3>
                <p className="text-gray-500">Start a task to see it here</p>
              </Card>
            ) : (
              <div className="space-y-4">
                {inProgressTasks.map(task => (
                  <TaskCard
                    key={task._id}
                    task={task}
                    expanded={expandedTaskId === task._id}
                    onToggleExpand={() => toggleExpandTask(task._id)}
                    onComplete={() => setSelectedTask(task)}
                    onTakePhoto={() => handleOpenCamera(task._id)}
                    onMessageHost={() => handleMessageHost(task)}
                    sendingPhoto={sendingPhoto && cameraTaskId === task._id}
                    getStatusColor={getStatusColor}
                    getServiceTypeIcon={getServiceTypeIcon}
                    showPhotos
                  />
                ))}
              </div>
            )}
          </TabsContent>

          {/* Completed Tab */}
          <TabsContent value="completed">
            {completedTasks.length === 0 ? (
              <Card className="p-8 text-center">
                <CheckCircle2 size={48} className="mx-auto text-gray-400 mb-4" />
                <h3 className="text-xl font-semibold text-gray-700">No completed tasks yet</h3>
                <p className="text-gray-500">Completed tasks will appear here</p>
              </Card>
            ) : (
              <div className="space-y-4">
                {completedTasks.map(task => (
                  <TaskCard
                    key={task._id}
                    task={task}
                    expanded={expandedTaskId === task._id}
                    onToggleExpand={() => toggleExpandTask(task._id)}
                    onMessageHost={() => handleMessageHost(task)}
                    getStatusColor={getStatusColor}
                    getServiceTypeIcon={getServiceTypeIcon}
                    showPhotos
                    onViewPhoto={(url) => setFullscreenPhoto(url)}
                  />
                ))}
              </div>
            )}
          </TabsContent>

          {/* Availability Tab */}
          <TabsContent value="availability">
            <Card>
              <CardHeader>
                <CardTitle>Your Availability</CardTitle>
              </CardHeader>
              <CardContent>
                <AvailabilityEditor 
                  availability={availability} 
                  onSave={async (newAvailability) => {
                    try {
                      const token = getAuthToken();
                      await providerAPI.updateAvailability(newAvailability, token);
                      setAvailability(newAvailability);
                      toast({ title: 'Success', description: 'Availability updated' });
                    } catch (error) {
                      toast({ title: 'Error', description: 'Failed to update availability', variant: 'destructive' });
                    }
                  }}
                />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Quote Requests Tab */}
          <TabsContent value="quotes">
            {quotesLoading ? (
              <Card className="p-8 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1F3A68] mx-auto"></div>
                <p className="mt-3 text-gray-500">Loading available requests...</p>
              </Card>
            ) : quoteRequests.length === 0 ? (
              <Card className="p-8 text-center">
                <FileText size={48} className="mx-auto text-gray-400 mb-4" />
                <h3 className="text-xl font-semibold text-gray-700">No Quote Requests Available</h3>
                <p className="text-gray-500">New service requests matching your profile will appear here</p>
              </Card>
            ) : (
              <div className="space-y-4">
                {quoteRequests.map(req => (
                  <Card key={req._id} className="hover:shadow-lg transition-shadow">
                    <CardContent className="p-6">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="text-lg font-semibold">{req.title}</h3>
                            {req.hasQuoted && (
                              <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">Quoted</span>
                            )}
                          </div>
                          <p className="text-sm text-gray-500 capitalize mb-2">{(req.category || '').replace(/_/g, ' ')}{req.subcategory ? ` — ${req.subcategory.replace(/_/g, ' ')}` : ''}</p>
                          <p className="text-gray-600 mb-3">{req.description}</p>
                          <div className="flex flex-wrap gap-3 text-sm text-gray-500">
                            {req.location?.postcode && (
                              <span className="flex items-center gap-1"><MapPin size={14} /> {req.location.postcode}</span>
                            )}
                            {req.preferredDate && (
                              <span className="flex items-center gap-1"><Calendar size={14} /> {new Date(req.preferredDate).toLocaleDateString('en-GB')}</span>
                            )}
                            {(req.budget?.min || req.budget?.max) && (
                              <span className="flex items-center gap-1">
                                <DollarSign size={14} />
                                Budget: {req.budget.min ? `£${req.budget.min}` : ''}{req.budget.min && req.budget.max ? '–' : ''}{req.budget.max ? `£${req.budget.max}` : ''}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="text-right text-xs text-gray-400">
                          {req.quotesCount || 0} quote{(req.quotesCount || 0) !== 1 ? 's' : ''}
                        </div>
                      </div>

                      {/* Quote Submission Form */}
                      {!req.hasQuoted && (
                        <div className="mt-4 pt-4 border-t">
                          <h4 className="text-sm font-semibold text-gray-700 mb-3">Submit Your Quote</h4>
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
                            <div>
                              <label className="text-xs text-gray-500 block mb-1">Amount (£) *</label>
                              <Input
                                type="number"
                                placeholder="e.g. 150"
                                min="1"
                                value={quoteForm[req._id]?.amount || ''}
                                onChange={e => setQuoteForm(prev => ({ ...prev, [req._id]: { ...prev[req._id], amount: e.target.value } }))}
                              />
                            </div>
                            <div>
                              <label className="text-xs text-gray-500 block mb-1">Duration</label>
                              <Input
                                placeholder="e.g. 3 hours"
                                value={quoteForm[req._id]?.estimatedDuration || ''}
                                onChange={e => setQuoteForm(prev => ({ ...prev, [req._id]: { ...prev[req._id], estimatedDuration: e.target.value } }))}
                              />
                            </div>
                            <div>
                              <label className="text-xs text-gray-500 block mb-1">Available Date</label>
                              <Input
                                type="date"
                                value={quoteForm[req._id]?.availableDate || ''}
                                onChange={e => setQuoteForm(prev => ({ ...prev, [req._id]: { ...prev[req._id], availableDate: e.target.value } }))}
                              />
                            </div>
                            <div className="flex items-end">
                              <Button
                                className="bg-[#1F3A68] hover:bg-[#1F3A68]/90 w-full"
                                disabled={submittingQuote === req._id}
                                onClick={() => handleSubmitQuote(req._id)}
                              >
                                {submittingQuote === req._id ? (
                                  <Loader2 size={16} className="animate-spin mr-1" />
                                ) : (
                                  <Send size={16} className="mr-1" />
                                )}
                                Send Quote
                              </Button>
                            </div>
                          </div>
                          <div>
                            <label className="text-xs text-gray-500 block mb-1">Description</label>
                            <Textarea
                              placeholder="Describe what's included in your quote..."
                              rows={2}
                              value={quoteForm[req._id]?.description || ''}
                              onChange={e => setQuoteForm(prev => ({ ...prev, [req._id]: { ...prev[req._id], description: e.target.value } }))}
                            />
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Job Board Tab */}
          <TabsContent value="jobboard">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <CardTitle className="flex items-center gap-2 text-[#1F3A68]">
                    <Search size={20} />
                    Job Board
                  </CardTitle>
                  <Button variant="outline" size="sm" onClick={() => fetchMarketplaceJobs(marketplaceFilter !== 'all' ? marketplaceFilter : null)}>
                    <Clock size={14} className="mr-1" /> Refresh
                  </Button>
                </div>
                <p className="text-sm text-gray-500 mt-1">Open jobs from hosts that match your service categories. Message a host to bid on a job.</p>

                {/* Category filter pills */}
                <div className="flex flex-wrap gap-2 mt-3">
                  <button
                    onClick={() => { setMarketplaceFilter('all'); fetchMarketplaceJobs(null); }}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                      marketplaceFilter === 'all' ? 'bg-[#1F3A68] text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    All Categories
                  </button>
                  {selectedServiceTypes.map(cat => (
                    <button
                      key={cat}
                      onClick={() => { setMarketplaceFilter(cat); fetchMarketplaceJobs(cat); }}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors capitalize ${
                        marketplaceFilter === cat ? 'bg-[#1F3A68] text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {cat.replace('_', ' ')}
                    </button>
                  ))}
                </div>
              </CardHeader>
              <CardContent>
                {marketplaceLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 size={24} className="animate-spin text-[#1F3A68]" />
                  </div>
                ) : marketplaceJobs.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <Briefcase size={48} className="mx-auto mb-4 text-gray-300" />
                    <p className="font-medium">No open jobs matching your categories</p>
                    <p className="text-sm mt-1">Check back soon or expand your service types in <button onClick={() => setActiveTab('profile')} className="text-[#1F3A68] underline font-medium">Profile settings</button>.</p>
                  </div>
                ) : (
                  <div className="grid md:grid-cols-2 gap-4">
                    {marketplaceJobs.map(job => (
                      <Card key={job.taskId} className="border hover:shadow-md transition-shadow">
                        <CardContent className="p-4">
                          {/* Header: type badge + priority */}
                          <div className="flex items-center justify-between mb-2">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-semibold capitalize ${
                              job.taskType === 'cleaning' ? 'bg-blue-100 text-blue-700' :
                              job.taskType === 'linen' ? 'bg-purple-100 text-purple-700' :
                              job.taskType === 'maintenance' ? 'bg-orange-100 text-orange-700' :
                              job.taskType === 'handyman' ? 'bg-amber-100 text-amber-700' :
                              job.taskType === 'inspection' ? 'bg-teal-100 text-teal-700' :
                              job.taskType === 'garden' ? 'bg-green-100 text-green-700' :
                              job.taskType === 'decorating' ? 'bg-pink-100 text-pink-700' :
                              job.taskType === 'locksmith' ? 'bg-gray-100 text-gray-700' :
                              job.taskType === 'pest_control' ? 'bg-red-100 text-red-700' :
                              job.taskType === 'removals' ? 'bg-indigo-100 text-indigo-700' :
                              job.taskType === 'security' ? 'bg-slate-100 text-slate-700' :
                              job.taskType === 'waste' ? 'bg-stone-100 text-stone-700' :
                              'bg-gray-100 text-gray-700'
                            }`}>
                              {job.taskType?.replace('_', ' ')}
                            </span>
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                              job.priority === 'urgent' ? 'bg-red-100 text-red-700' :
                              job.priority === 'high' ? 'bg-orange-100 text-orange-700' :
                              job.priority === 'normal' ? 'bg-blue-100 text-blue-700' :
                              'bg-gray-100 text-gray-600'
                            }`}>
                              {job.priority}
                            </span>
                          </div>

                          {/* Title & description */}
                          <h4 className="font-semibold text-[#1F3A68] mb-1">{job.title}</h4>
                          <p className="text-sm text-gray-600 mb-3 line-clamp-2">{job.description || 'No description provided.'}</p>

                          {/* Details */}
                          <div className="space-y-1 text-xs text-gray-500 mb-3">
                            <div className="flex items-center gap-1.5">
                              <MapPin size={12} />
                              <span>{job.propertyArea || job.propertyTitle || 'Location not specified'}</span>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <Calendar size={12} />
                              <span>{job.scheduledDate ? new Date(job.scheduledDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : 'Flexible'}</span>
                              {job.scheduledTime && <span className="ml-1">at {job.scheduledTime}</span>}
                            </div>
                            <div className="flex items-center gap-1.5">
                              <User size={12} />
                              <span>Posted by {job.hostName}</span>
                            </div>
                          </div>

                          {/* Amount + Bid button */}
                          <div className="flex items-center justify-between pt-2 border-t">
                            <div>
                              {job.amount > 0 ? (
                                <span className="text-lg font-bold text-green-700">£{job.amount.toFixed(2)}</span>
                              ) : (
                                <span className="text-sm text-gray-400 italic">Open to quotes</span>
                              )}
                            </div>
                            <Button
                              size="sm"
                              className="bg-[#1F3A68] hover:bg-[#1F3A68]/90"
                              onClick={() => handleBidOnJob(job)}
                            >
                              <MessageSquare size={14} className="mr-1" />
                              Message Host & Bid
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Earnings Tab */}
          <TabsContent value="earnings">
            {earningsLoading ? (
              <Card className="p-8 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1F3A68] mx-auto"></div>
                <p className="mt-3 text-gray-500">Loading earnings...</p>
              </Card>
            ) : (
              <div className="space-y-6">
                {/* Earnings Summary Cards */}
                <div className="grid md:grid-cols-3 gap-6">
                  <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
                    <CardContent className="p-6 text-center">
                      <DollarSign size={28} className="mx-auto mb-2" />
                      <div className="text-3xl font-bold">£{(earnings?.totalEarnings || 0).toFixed(2)}</div>
                      <div className="text-green-100 text-sm">Total Earnings</div>
                    </CardContent>
                  </Card>
                  <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white">
                    <CardContent className="p-6 text-center">
                      <DollarSign size={28} className="mx-auto mb-2" />
                      <div className="text-3xl font-bold">£{(earnings?.pendingPayout || 0).toFixed(2)}</div>
                      <div className="text-amber-100 text-sm">Pending Payout</div>
                    </CardContent>
                  </Card>
                  <Card className="bg-gradient-to-br from-[#1F3A68] to-[#2d4f8a] text-white">
                    <CardContent className="p-6 text-center">
                      <Briefcase size={28} className="mx-auto mb-2" />
                      <div className="text-3xl font-bold">{earnings?.completedJobs || 0}</div>
                      <div className="text-blue-100 text-sm">Completed Jobs</div>
                    </CardContent>
                  </Card>
                </div>

                {/* Commission Info */}
                <Card className="border-blue-200 bg-blue-50">
                  <CardContent className="p-4 text-sm text-blue-800">
                    <p className="font-medium mb-1">Commission Structure</p>
                    <p>A 5% platform fee is deducted from all jobs. This is one of the lowest rates in the UK market. Your net earnings are displayed above.</p>
                  </CardContent>
                </Card>

                {/* Recent Ledger Entries */}
                {earnings?.recentEntries?.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-[#1F3A68]">Recent Earnings</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {earnings.recentEntries.map((entry, i) => (
                          <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div>
                              <p className="font-medium text-gray-900">{entry.description}</p>
                              <p className="text-xs text-gray-400">{entry.createdAt ? new Date(entry.createdAt).toLocaleDateString('en-GB') : ''}</p>
                            </div>
                            <div className="text-right">
                              <p className="font-bold text-green-600">£{(entry.netAmount || 0).toFixed(2)}</p>
                              <p className="text-xs text-gray-400">Fee: £{(entry.platformFee || 0).toFixed(2)}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics">
            {analyticsLoading ? (
              <Card className="p-8 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1F3A68] mx-auto"></div>
                <p className="mt-3 text-gray-500">Loading analytics...</p>
              </Card>
            ) : analyticsData ? (
              <div className="space-y-6">
                {/* Summary KPIs */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <Card>
                    <CardContent className="p-4 text-center">
                      <DollarSign size={24} className="mx-auto text-green-600 mb-1" />
                      <div className="text-2xl font-bold text-green-700">£{analyticsData.totalNet?.toFixed(2) || '0.00'}</div>
                      <div className="text-xs text-gray-500">Net Earnings</div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4 text-center">
                      <Briefcase size={24} className="mx-auto text-[#1F3A68] mb-1" />
                      <div className="text-2xl font-bold text-[#1F3A68]">{analyticsData.totalJobs || 0}</div>
                      <div className="text-xs text-gray-500">Total Jobs</div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4 text-center">
                      <Star size={24} className="mx-auto text-yellow-500 mb-1" />
                      <div className="text-2xl font-bold text-yellow-600">{analyticsData.averageRating || '0.0'}</div>
                      <div className="text-xs text-gray-500">Avg Rating</div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4 text-center">
                      <Clock size={24} className="mx-auto text-blue-600 mb-1" />
                      <div className="text-2xl font-bold text-blue-700">{analyticsData.onTimeRate || 100}%</div>
                      <div className="text-xs text-gray-500">On-Time Rate</div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4 text-center">
                      <Percent size={24} className="mx-auto text-red-500 mb-1" />
                      <div className="text-2xl font-bold text-red-600">5%</div>
                      <div className="text-xs text-gray-500">Platform Fee</div>
                    </CardContent>
                  </Card>
                </div>

                {/* Earnings by Service Type */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-[#1F3A68] flex items-center gap-2">
                      <BarChart3 size={20} /> Earnings by Service Type
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {Object.keys(analyticsData.earningsByType || {}).length > 0 ? (
                      <div className="space-y-4">
                        {Object.entries(analyticsData.earningsByType).map(([type, data]) => {
                          const maxGross = Math.max(...Object.values(analyticsData.earningsByType).map(d => d.gross));
                          return (
                            <div key={type} className="space-y-1">
                              <div className="flex justify-between items-center">
                                <span className="text-sm font-medium capitalize">{type.replace(/_/g, ' ')}</span>
                                <div className="text-right text-sm">
                                  <span className="font-bold text-green-700">£{data.net.toFixed(2)}</span>
                                  <span className="text-gray-400 ml-2">({data.count} jobs)</span>
                                </div>
                              </div>
                              <div className="w-full h-3 bg-gray-100 rounded-full overflow-hidden">
                                <div className="h-full rounded-full flex">
                                  <div
                                    className="bg-green-500 h-full"
                                    style={{ width: `${(data.net / maxGross) * 100}%` }}
                                  />
                                  <div
                                    className="bg-red-300 h-full"
                                    style={{ width: `${(data.commission / maxGross) * 100}%` }}
                                  />
                                </div>
                              </div>
                              <div className="flex gap-4 text-xs text-gray-400">
                                <span>Gross: £{data.gross.toFixed(2)}</span>
                                <span>Fee: £{data.commission.toFixed(2)}</span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-gray-400 text-center py-4">Complete jobs to see earnings breakdown by service type</p>
                    )}
                  </CardContent>
                </Card>

                {/* Monthly Earnings Trend */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-[#1F3A68] flex items-center gap-2">
                      <TrendingUp size={20} /> Monthly Earnings
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {Object.keys(analyticsData.earningsByMonth || {}).length > 0 ? (
                      <div className="space-y-3">
                        {Object.entries(analyticsData.earningsByMonth).map(([month, data]) => {
                          const maxNet = Math.max(...Object.values(analyticsData.earningsByMonth).map(d => d.net));
                          const monthDate = new Date(month + '-01');
                          const monthLabel = monthDate.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
                          return (
                            <div key={month} className="flex items-center gap-4">
                              <span className="text-sm text-gray-600 w-20 flex-shrink-0">{monthLabel}</span>
                              <div className="flex-1 h-6 bg-gray-100 rounded-full overflow-hidden relative">
                                <div
                                  className="h-full bg-gradient-to-r from-[#1F3A68] to-[#2A4F8F] rounded-full transition-all"
                                  style={{ width: `${maxNet > 0 ? (data.net / maxNet) * 100 : 0}%` }}
                                />
                                <span className="absolute inset-0 flex items-center justify-center text-xs font-medium text-white drop-shadow-sm">
                                  {data.net > 0 ? `£${data.net.toFixed(0)}` : ''}
                                </span>
                              </div>
                              <span className="text-sm font-semibold text-gray-700 w-16 text-right">{data.count} jobs</span>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-gray-400 text-center py-4">Complete jobs to see monthly trends</p>
                    )}
                  </CardContent>
                </Card>

                {/* Commission Info */}
                <Card className="border-blue-200 bg-blue-50">
                  <CardContent className="p-4 text-sm text-blue-800">
                    <p className="font-medium mb-1">About Your Platform Fee</p>
                    <p>Ariv charges a 5% platform fee (excl. VAT) on all completed jobs — one of the lowest rates in the UK market. All amounts shown exclude VAT. If you are VAT registered, please ensure your VAT number is up to date in your Profile tab.</p>
                  </CardContent>
                </Card>
              </div>
            ) : (
              <Card className="p-8 text-center">
                <BarChart3 size={48} className="mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500">Complete jobs to see your business analytics</p>
              </Card>
            )}
          </TabsContent>

          {/* Messages Tab - Full Chat UI */}
          <TabsContent value="messages">
            <Card>
              <CardHeader>
                <CardTitle className="text-[#1F3A68]">Messages</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="h-[600px]">
                  <MessagingPanel userRole="provider" autoSelectConversationId={selectedConversationId} onUnreadChange={handleUnreadChange} />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Compliance Tab */}
          <TabsContent value="compliance">
            <CompliancePanel
              compliance={dashboard?.profile?.compliance}
              complianceStatus={dashboard?.profile?.complianceStatus}
              onUpload={handleDocumentUpload}
              uploading={docUploading}
            />
          </TabsContent>

          {/* Profile Tab - Checkatrade-Style */}
          <TabsContent value="profile">
            <div className="space-y-6">

              {/* Card 1: Profile Header */}
              <Card className="overflow-hidden">
                <div className="bg-gradient-to-r from-[#1F3A68] to-[#2A4F8F] p-6">
                  <div className="flex items-start gap-6">
                    {/* Profile Photo */}
                    <div className="relative group">
                      <div className="w-28 h-28 rounded-full border-4 border-white shadow-lg overflow-hidden bg-white flex items-center justify-center">
                        {profilePhotoUrl ? (
                          <img src={profilePhotoUrl} alt="Profile" className="w-full h-full object-cover" />
                        ) : (
                          <User size={40} className="text-gray-300" />
                        )}
                      </div>
                      <label className="absolute inset-0 rounded-full bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
                        <Camera size={24} className="text-white" />
                        <input
                          type="file"
                          accept="image/jpeg,image/png,image/webp"
                          className="hidden"
                          onChange={e => e.target.files?.[0] && handleProfilePhotoUpload(e.target.files[0])}
                        />
                      </label>
                      {uploadingProfilePhoto && (
                        <div className="absolute inset-0 rounded-full bg-black/50 flex items-center justify-center">
                          <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        </div>
                      )}
                    </div>
                    {/* Name, tagline, rating */}
                    <div className="flex-1 text-white">
                      <h2 className="text-2xl font-bold">
                        {marketplaceProfile.businessName || profileForm.name || 'Your Business Name'}
                      </h2>
                      <p className="text-white/80 mt-1">
                        {marketplaceProfile.tagline || 'Add a tagline to describe your services'}
                      </p>
                      <div className="flex items-center gap-3 mt-3">
                        <div className="flex items-center gap-1">
                          {[1,2,3,4,5].map(s => (
                            <Star key={s} size={18} className={s <= Math.round(dashboard?.stats?.rating || 0) ? 'fill-yellow-400 text-yellow-400' : 'text-white/30'} />
                          ))}
                        </div>
                        <span className="text-lg font-semibold">{dashboard?.stats?.rating?.toFixed(1) || '0.0'}</span>
                        <span className="text-white/60 text-sm">({dashboard?.profile?.completedTasks || 0} jobs completed)</span>
                      </div>
                      {/* Verification badges */}
                      <div className="flex flex-wrap gap-2 mt-3">
                        {dashboard?.profile?.compliance?.dbs_check === 'approved' && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-500/20 text-green-200 rounded-full text-xs font-medium">
                            <BadgeCheck size={12} /> DBS Checked
                          </span>
                        )}
                        {dashboard?.profile?.compliance?.insurance === 'approved' && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-500/20 text-green-200 rounded-full text-xs font-medium">
                            <BadgeCheck size={12} /> Insured
                          </span>
                        )}
                        {companyDetails.companyNumber && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-500/20 text-blue-200 rounded-full text-xs font-medium">
                            <Building2 size={12} /> Registered Company
                          </span>
                        )}
                        {taxDetails.hasVatNumber && taxDetails.vatNumber && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-500/20 text-blue-200 rounded-full text-xs font-medium">
                            <BadgeCheck size={12} /> VAT Registered
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                {/* Public profile toggle */}
                <CardContent className="pt-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-semibold text-gray-900">Public Profile</h4>
                      <p className="text-sm text-gray-500">Make your profile visible on the service marketplace</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={marketplaceProfile.publicProfile}
                        onChange={async e => {
                          const newVal = e.target.checked;
                          setMarketplaceProfile(prev => ({ ...prev, publicProfile: newVal }));
                          try {
                            const token = getAuthToken();
                            await providerAPI.updateProfile({ publicProfile: newVal }, token);
                            toast({ title: newVal ? 'Profile is now public' : 'Profile is now private', description: newVal ? 'Hosts can find you on the marketplace' : 'Your profile is hidden from the marketplace' });
                          } catch (err) {
                            setMarketplaceProfile(prev => ({ ...prev, publicProfile: !newVal }));
                            toast({ title: 'Error', description: 'Failed to update visibility', variant: 'destructive' });
                          }
                        }}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#1F3A68]/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#1F3A68]"></div>
                    </label>
                  </div>
                </CardContent>
              </Card>

              {/* Card 2: Ratings Breakdown */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-[#1F3A68] flex items-center gap-2">
                    <Star size={20} /> Ratings & Reviews
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Overall rating */}
                    <div className="text-center p-6 bg-gray-50 rounded-xl">
                      <p className="text-5xl font-bold text-[#1F3A68]">{dashboard?.stats?.rating?.toFixed(1) || '0.0'}</p>
                      <div className="flex justify-center gap-1 mt-2">
                        {[1,2,3,4,5].map(s => (
                          <Star key={s} size={22} className={s <= Math.round(dashboard?.stats?.rating || 0) ? 'fill-[#1F3A68] text-[#1F3A68]' : 'text-gray-200'} />
                        ))}
                      </div>
                      <p className="text-sm text-gray-500 mt-2">Based on {dashboard?.profile?.completedTasks || 0} reviews</p>
                    </div>
                    {/* Category breakdown */}
                    <div className="space-y-4">
                      {[
                        { label: 'Quality of Work', key: 'quality' },
                        { label: 'Reliability', key: 'reliability' },
                        { label: 'Communication', key: 'communication' },
                        { label: 'Value for Money', key: 'value' },
                      ].map(cat => {
                        const catRating = dashboard?.profile?.ratingBreakdown?.[cat.key] || dashboard?.stats?.rating || 0;
                        return (
                          <div key={cat.key}>
                            <div className="flex justify-between text-sm mb-1">
                              <span className="text-gray-700 font-medium">{cat.label}</span>
                              <span className="text-[#1F3A68] font-semibold">{Number(catRating).toFixed(1)}</span>
                            </div>
                            <div className="w-full h-2.5 bg-gray-100 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-[#1F3A68] rounded-full transition-all"
                                style={{ width: `${(catRating / 5) * 100}%` }}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Card 3: About & Experience */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-[#1F3A68] flex items-center gap-2">
                    <Award size={20} /> About & Experience
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-5">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium text-gray-700">Business / Trading Name</label>
                        <Input
                          value={marketplaceProfile.businessName}
                          onChange={e => setMarketplaceProfile(prev => ({ ...prev, businessName: e.target.value }))}
                          className="mt-1"
                          placeholder="Your business or trading name"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-700">Tagline</label>
                        <Input
                          value={marketplaceProfile.tagline}
                          onChange={e => setMarketplaceProfile(prev => ({ ...prev, tagline: e.target.value }))}
                          className="mt-1"
                          placeholder="e.g. Professional cleaning with a personal touch"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="text-sm font-medium text-gray-700">About You / Bio</label>
                      <Textarea
                        value={profileForm.bio}
                        onChange={e => setProfileForm({ ...profileForm, bio: e.target.value })}
                        className="mt-1"
                        placeholder="Tell hosts about your experience, approach, and what makes you stand out..."
                        rows={4}
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium text-gray-700">Years of Experience</label>
                        <Input
                          type="number"
                          min="0"
                          value={marketplaceProfile.yearsExperience}
                          onChange={e => setMarketplaceProfile(prev => ({ ...prev, yearsExperience: e.target.value }))}
                          className="mt-1"
                          placeholder="e.g. 5"
                        />
                      </div>
                      <div className="relative">
                        <label className="text-sm font-medium text-gray-700">Service Categories (select all that apply)</label>
                        <div
                          className="mt-1 min-h-[42px] px-3 py-2 border rounded-lg cursor-pointer bg-white flex flex-wrap gap-1.5 items-center"
                          onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}
                        >
                          {selectedServiceTypes.length === 0 ? (
                            <span className="text-gray-400 text-sm">Click to select service categories...</span>
                          ) : (
                            selectedServiceTypes.map(type => {
                              const cat = categories.find(c => c.id === type);
                              return (
                                <span key={type} className="inline-flex items-center gap-1 px-2.5 py-0.5 bg-[#1F3A68] text-white rounded-full text-xs font-medium">
                                  {cat?.label || type}
                                  <button
                                    onClick={e => {
                                      e.stopPropagation();
                                      setSelectedServiceTypes(prev => prev.filter(t => t !== type));
                                    }}
                                    className="ml-0.5 hover:text-red-300"
                                  >
                                    <X size={10} />
                                  </button>
                                </span>
                              );
                            })
                          )}
                          <ChevronDown size={16} className="ml-auto text-gray-400 flex-shrink-0" />
                        </div>
                        {showCategoryDropdown && (
                          <div className="absolute z-20 mt-1 w-full bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                            {categories.map(cat => {
                              const isSelected = selectedServiceTypes.includes(cat.id);
                              return (
                                <label
                                  key={cat.id}
                                  className={`flex items-center gap-3 px-4 py-2.5 cursor-pointer hover:bg-gray-50 ${isSelected ? 'bg-[#1F3A68]/5' : ''}`}
                                >
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => {
                                      setSelectedServiceTypes(prev =>
                                        isSelected ? prev.filter(t => t !== cat.id) : [...prev, cat.id]
                                      );
                                    }}
                                    className="rounded border-gray-300 text-[#1F3A68] focus:ring-[#1F3A68]"
                                  />
                                  <span className="text-sm font-medium">{cat.label}</span>
                                  <span className="text-xs text-gray-400 ml-auto">{cat.subcategories?.length || 0} subcategories</span>
                                </label>
                              );
                            })}
                            <div className="px-4 py-2 border-t bg-gray-50">
                              <button
                                onClick={() => setShowCategoryDropdown(false)}
                                className="text-sm text-[#1F3A68] font-medium"
                              >
                                Done
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Qualifications */}
                    <div>
                      <label className="text-sm font-medium text-gray-700">Qualifications & Certifications</label>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {(marketplaceProfile.qualifications || []).map(q => (
                          <span key={q} className="inline-flex items-center gap-1 px-3 py-1 bg-[#1F3A68]/10 text-[#1F3A68] rounded-full text-sm font-medium">
                            <Award size={12} /> {q}
                            <button onClick={() => removeQualification(q)} className="ml-1 hover:text-red-500 transition-colors">
                              <X size={12} />
                            </button>
                          </span>
                        ))}
                      </div>
                      <div className="flex gap-2 mt-2">
                        <Input
                          value={newQualification}
                          onChange={e => setNewQualification(e.target.value)}
                          placeholder="e.g. City & Guilds Level 3, Gas Safe"
                          className="flex-1"
                          onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addQualification())}
                        />
                        <Button onClick={addQualification} variant="outline" className="border-[#1F3A68] text-[#1F3A68]">
                          Add
                        </Button>
                      </div>
                    </div>

                    <div className="flex gap-3">
                      <Button
                        onClick={handleSaveProfile}
                        disabled={profileSaving}
                        className="bg-[#1F3A68] hover:bg-[#1F3A68]/90"
                      >
                        <Save size={16} className="mr-2" />
                        {profileSaving ? 'Saving...' : 'Save Profile'}
                      </Button>
                      <Button
                        onClick={handleSaveMarketplaceProfile}
                        disabled={profileSaving}
                        variant="outline"
                        className="border-[#1F3A68] text-[#1F3A68]"
                      >
                        <Save size={16} className="mr-2" />
                        {profileSaving ? 'Saving...' : 'Save Marketplace Info'}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Card 4: Portfolio Gallery */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-[#1F3A68] flex items-center gap-2">
                    <ImagePlus size={20} /> Portfolio Gallery
                    <span className="text-sm font-normal text-gray-400 ml-auto">
                      {portfolioPhotos.length}/12 uploaded
                    </span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-500 mb-4">
                    Showcase your best work to attract more clients. Upload photos or take new ones.
                  </p>

                  {showPortfolioCamera ? (
                    <div className="mb-4">
                      <LivePhotoCapture
                        onCapture={handlePortfolioCapture}
                        onClose={() => setShowPortfolioCamera(false)}
                        label="Portfolio Photo"
                      />
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 mb-4">
                      {portfolioPhotos.map((photo, idx) => (
                        <div key={idx} className="relative group aspect-square rounded-lg overflow-hidden border bg-gray-50">
                          <img
                            src={typeof photo === 'string' ? photo : photo.url}
                            alt={`Portfolio ${idx + 1}`}
                            className="w-full h-full object-cover cursor-pointer"
                            onClick={() => window.open(typeof photo === 'string' ? photo : photo.url, '_blank')}
                          />
                          <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                            <a
                              href={typeof photo === 'string' ? photo : photo.url}
                              download={`portfolio-${idx + 1}.jpg`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-2 bg-white/90 rounded-full hover:bg-white transition-colors"
                              onClick={e => e.stopPropagation()}
                            >
                              <Download size={16} className="text-[#1F3A68]" />
                            </a>
                            <button
                              onClick={() => handleDeletePortfolioPhoto(typeof photo === 'string' ? photo : photo.url)}
                              disabled={deletingPortfolioPhoto === (typeof photo === 'string' ? photo : photo.url)}
                              className="p-2 bg-white/90 rounded-full hover:bg-red-50 transition-colors"
                            >
                              <Trash2 size={16} className="text-red-500" />
                            </button>
                          </div>
                        </div>
                      ))}

                      {portfolioPhotos.length < 12 && (
                        <>
                          {/* Upload from file */}
                          <label className="aspect-square rounded-lg border-2 border-dashed border-gray-300 flex flex-col items-center justify-center cursor-pointer hover:border-[#1F3A68] hover:bg-[#1F3A68]/5 transition-colors">
                            {uploadingPortfolio ? (
                              <div className="w-6 h-6 border-2 border-[#1F3A68] border-t-transparent rounded-full animate-spin" />
                            ) : (
                              <>
                                <ImagePlus size={24} className="text-gray-400" />
                                <span className="text-xs text-gray-400 mt-1">Upload</span>
                              </>
                            )}
                            <input
                              type="file"
                              accept="image/jpeg,image/png,image/webp"
                              className="hidden"
                              onChange={e => e.target.files?.[0] && handlePortfolioUpload(e.target.files[0])}
                            />
                          </label>
                          {/* Camera capture */}
                          <button
                            onClick={() => setShowPortfolioCamera(true)}
                            className="aspect-square rounded-lg border-2 border-dashed border-gray-300 flex flex-col items-center justify-center hover:border-[#1F3A68] hover:bg-[#1F3A68]/5 transition-colors"
                          >
                            <Camera size={24} className="text-gray-400" />
                            <span className="text-xs text-gray-400 mt-1">Camera</span>
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Card 5: Contact & Service Area */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-[#1F3A68] flex items-center gap-2">
                    <MapPin size={20} /> Contact & Service Area
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-5">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                          <User size={14} /> Full Name
                        </label>
                        <Input
                          value={profileForm.name}
                          onChange={e => setProfileForm({ ...profileForm, name: e.target.value })}
                          className="mt-1"
                          placeholder="Your full name"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                          <Mail size={14} /> Email
                        </label>
                        <Input
                          value={profileForm.email}
                          onChange={e => setProfileForm({ ...profileForm, email: e.target.value })}
                          className="mt-1"
                          placeholder="your@email.com"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                          <Phone size={14} /> Phone
                        </label>
                        <Input
                          value={profileForm.phone}
                          onChange={e => setProfileForm({ ...profileForm, phone: e.target.value })}
                          className="mt-1"
                          placeholder="+44 7XXX XXX XXX"
                          type="tel"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium text-gray-700">Service Area (Postcodes)</label>
                        <Input
                          value={marketplaceProfile.serviceArea}
                          onChange={e => setMarketplaceProfile(prev => ({ ...prev, serviceArea: e.target.value }))}
                          className="mt-1"
                          placeholder="e.g. SW1, SW2, W1, EC1 (comma separated)"
                        />
                        <p className="text-xs text-gray-400 mt-1">Enter postcode prefixes separated by commas</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-700">Hourly Rate (£)</label>
                        <Input
                          type="number"
                          min="0"
                          step="0.50"
                          value={marketplaceProfile.hourlyRate}
                          onChange={e => setMarketplaceProfile(prev => ({ ...prev, hourlyRate: e.target.value }))}
                          className="mt-1"
                          placeholder="e.g. 25"
                          disabled={marketplaceProfile.customQuotesOnly}
                        />
                        <label className="flex items-center gap-2 cursor-pointer mt-2">
                          <input
                            type="checkbox"
                            checked={marketplaceProfile.customQuotesOnly}
                            onChange={async e => {
                              const newVal = e.target.checked;
                              setMarketplaceProfile(prev => ({ ...prev, customQuotesOnly: newVal }));
                              try {
                                const token = getAuthToken();
                                await providerAPI.updateProfile({ pricing: { customQuotesOnly: newVal } }, token);
                              } catch (err) {
                                setMarketplaceProfile(prev => ({ ...prev, customQuotesOnly: !newVal }));
                                toast({ title: 'Error', description: 'Failed to save setting', variant: 'destructive' });
                              }
                            }}
                            className="w-4 h-4 rounded"
                          />
                          <span className="text-sm text-gray-600">Custom quotes only</span>
                        </label>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Card 6: Company & Tax Details */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-[#1F3A68] flex items-center gap-2">
                    <Building2 size={20} /> Company & Tax Details
                  </CardTitle>
                  <p className="text-sm text-gray-500">Optional — fill in if you operate as a registered company</p>
                </CardHeader>
                <CardContent>
                  <div className="space-y-5">
                    {/* Sole trader vs Company */}
                    <div className="flex gap-4">
                      <label className={`flex-1 p-4 rounded-lg border-2 cursor-pointer transition-colors ${companyDetails.isSoleTrader ? 'border-[#1F3A68] bg-[#1F3A68]/5' : 'border-gray-200'}`}>
                        <input
                          type="radio"
                          name="businessType"
                          checked={companyDetails.isSoleTrader}
                          onChange={() => setCompanyDetails(prev => ({ ...prev, isSoleTrader: true }))}
                          className="sr-only"
                        />
                        <div className="flex items-center gap-2">
                          <User size={18} className={companyDetails.isSoleTrader ? 'text-[#1F3A68]' : 'text-gray-400'} />
                          <span className={`font-medium ${companyDetails.isSoleTrader ? 'text-[#1F3A68]' : 'text-gray-600'}`}>Sole Trader / Individual</span>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">Operating in your own name</p>
                      </label>
                      <label className={`flex-1 p-4 rounded-lg border-2 cursor-pointer transition-colors ${!companyDetails.isSoleTrader ? 'border-[#1F3A68] bg-[#1F3A68]/5' : 'border-gray-200'}`}>
                        <input
                          type="radio"
                          name="businessType"
                          checked={!companyDetails.isSoleTrader}
                          onChange={() => setCompanyDetails(prev => ({ ...prev, isSoleTrader: false }))}
                          className="sr-only"
                        />
                        <div className="flex items-center gap-2">
                          <Building2 size={18} className={!companyDetails.isSoleTrader ? 'text-[#1F3A68]' : 'text-gray-400'} />
                          <span className={`font-medium ${!companyDetails.isSoleTrader ? 'text-[#1F3A68]' : 'text-gray-600'}`}>Registered Company</span>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">Limited company or partnership</p>
                      </label>
                    </div>

                    {/* Company fields — shown only when not sole trader */}
                    {!companyDetails.isSoleTrader && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                        <div>
                          <label className="text-sm font-medium text-gray-700">Trading Name</label>
                          <Input
                            value={companyDetails.tradingName}
                            onChange={e => setCompanyDetails(prev => ({ ...prev, tradingName: e.target.value }))}
                            className="mt-1"
                            placeholder="Trading name"
                          />
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-700">Registered Company Name</label>
                          <Input
                            value={companyDetails.companyName}
                            onChange={e => setCompanyDetails(prev => ({ ...prev, companyName: e.target.value }))}
                            className="mt-1"
                            placeholder="As registered with Companies House"
                          />
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-700">Company Number</label>
                          <Input
                            value={companyDetails.companyNumber}
                            onChange={e => setCompanyDetails(prev => ({ ...prev, companyNumber: e.target.value }))}
                            className="mt-1"
                            placeholder="e.g. 12345678"
                          />
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-700">Registered Address</label>
                          <Input
                            value={companyDetails.registeredAddress}
                            onChange={e => setCompanyDetails(prev => ({ ...prev, registeredAddress: e.target.value }))}
                            className="mt-1"
                            placeholder="Company registered address"
                          />
                        </div>
                      </div>
                    )}

                    {/* Tax details */}
                    <div className="space-y-4">
                      <div>
                        <label className="text-sm font-medium text-gray-700">Unique Taxpayer Reference (UTR)</label>
                        <Input
                          value={taxDetails.utrNumber}
                          onChange={e => setTaxDetails(prev => ({ ...prev, utrNumber: e.target.value }))}
                          className="mt-1"
                          placeholder="10-digit UTR number"
                        />
                      </div>
                      <div>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={taxDetails.hasVatNumber}
                            onChange={e => setTaxDetails(prev => ({ ...prev, hasVatNumber: e.target.checked }))}
                            className="w-4 h-4 rounded"
                          />
                          <span className="text-sm font-medium text-gray-700">I am VAT registered</span>
                        </label>
                        {taxDetails.hasVatNumber && (
                          <Input
                            value={taxDetails.vatNumber}
                            onChange={e => setTaxDetails(prev => ({ ...prev, vatNumber: e.target.value }))}
                            className="mt-2"
                            placeholder="GB 123 4567 89"
                          />
                        )}
                      </div>
                    </div>

                    {/* Gentle tax encouragement */}
                    {!taxDetails.utrNumber && (
                      <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-lg border border-blue-100">
                        <AlertCircle size={18} className="text-blue-500 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-sm text-blue-800">Adding your UTR and VAT details helps build trust with hosts and shows you operate professionally.</p>
                          <p className="text-xs text-blue-600 mt-1">This is optional — you can add it anytime.</p>
                        </div>
                      </div>
                    )}

                    <Button
                      onClick={handleSaveCompanyAndTax}
                      disabled={profileSaving}
                      className="bg-[#1F3A68] hover:bg-[#1F3A68]/90"
                    >
                      <Save size={16} className="mr-2" />
                      {profileSaving ? 'Saving...' : 'Save Company & Tax Details'}
                    </Button>
                  </div>
                </CardContent>
              </Card>

            </div>
          </TabsContent>

          <TabsContent value="referrals">
            <ReferralDashboard />
          </TabsContent>

          <TabsContent value="faq">
            <FAQFeedback />
          </TabsContent>
        </Tabs>
      </main>

      {/* Complete Task Modal — shows photos taken for review before completing */}
      {selectedTask && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <CardTitle>Complete Task</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm font-medium">Task: {selectedTask.title}</label>
                <p className="text-xs text-gray-500 mt-1">{selectedTask.propertyTitle} — {selectedTask.propertyAddress}</p>
              </div>

              {/* Review evidence photos before completing */}
              {(selectedTask.evidencePhotos?.length > 0 || selectedTask.completionPhotos?.length > 0) && (
                <div>
                  <label className="text-sm font-medium flex items-center gap-1 mb-2">
                    <Camera size={14} /> Evidence Photos Taken ({selectedTask.evidencePhotos?.length || selectedTask.completionPhotos?.length || 0})
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    {(selectedTask.evidencePhotos || []).map((photo, idx) => (
                      <div key={idx} className="relative group cursor-pointer" onClick={() => setFullscreenPhoto(`${API_URL}${photo.url}`)}>
                        <img src={`${API_URL}${photo.url}`} alt={`Evidence ${idx + 1}`} className="w-full h-24 object-cover rounded-lg border" />
                        <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[10px] px-1 py-0.5 rounded-b-lg truncate">
                          {photo.label} — {photo.timestampText || new Date(photo.capturedAt).toLocaleString('en-GB')}
                        </div>
                        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                          <ZoomIn className="w-5 h-5 text-white drop-shadow-lg" />
                        </div>
                      </div>
                    ))}
                    {/* Fallback for legacy completionPhotos without metadata */}
                    {!selectedTask.evidencePhotos?.length && (selectedTask.completionPhotos || []).map((url, idx) => (
                      <div key={idx} className="relative cursor-pointer" onClick={() => setFullscreenPhoto(`${API_URL}${url}`)}>
                        <img src={`${API_URL}${url}`} alt={`Photo ${idx + 1}`} className="w-full h-24 object-cover rounded-lg border" />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {(!selectedTask.evidencePhotos?.length && !selectedTask.completionPhotos?.length) && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800 flex items-start gap-2">
                  <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium">Photo evidence is required</p>
                    <p className="text-red-600 mt-1">You must take at least one photo as evidence before marking this task as complete. This protects both you and the host.</p>
                  </div>
                </div>
              )}

              <Button
                variant="outline"
                size="sm"
                onClick={() => { setSelectedTask(null); handleOpenCamera(selectedTask._id); }}
                className="w-full border-[#1F3A68] text-[#1F3A68]"
              >
                <Camera size={14} className="mr-1" />
                {(selectedTask.evidencePhotos?.length || selectedTask.completionPhotos?.length) ? 'Take More Photos' : 'Take Evidence Photos'}
              </Button>

              <div>
                <label className="text-sm font-medium">Completion Notes</label>
                <Textarea
                  placeholder="Add any notes about the completed task..."
                  value={completionNotes}
                  onChange={(e) => setCompletionNotes(e.target.value)}
                  className="mt-1"
                  rows={4}
                />
              </div>
              <div className="flex gap-3">
                <Button
                  className={`flex-1 ${(selectedTask.evidencePhotos?.length || selectedTask.completionPhotos?.length) ? 'bg-green-600 hover:bg-green-700' : 'bg-gray-300 cursor-not-allowed'}`}
                  disabled={!selectedTask.evidencePhotos?.length && !selectedTask.completionPhotos?.length}
                  onClick={() => handleCompleteTask(selectedTask._id)}
                >
                  <Check size={16} className="mr-1" /> Mark Complete
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setSelectedTask(null);
                    setCompletionNotes('');
                  }}
                >
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Live Camera Overlay */}
      {showCamera && (
        <LivePhotoCapture
          onCapture={handleCapturePhoto}
          onClose={() => { setShowCamera(false); setCameraTaskId(null); }}
          role="provider"
        />
      )}

      {/* Fullscreen Photo Viewer */}
      {fullscreenPhoto && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center" onClick={() => setFullscreenPhoto(null)}>
          <Button
            variant="ghost"
            size="sm"
            className="absolute top-4 right-4 text-white hover:bg-white/20"
            onClick={() => setFullscreenPhoto(null)}
          >
            <X className="w-6 h-6" />
          </Button>
          <img src={fullscreenPhoto} alt="Full size" className="max-w-[95vw] max-h-[95vh] object-contain" />
        </div>
      )}
    </div>
  );
};

// Task Card Component — clickable, expandable with full job details
const TaskCard = ({ task, expanded, onToggleExpand, onAccept, onStart, onComplete, onTakePhoto, onMessageHost, sendingPhoto, getStatusColor, getServiceTypeIcon, showPhotos, onViewPhoto }) => {
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
  const photoCount = task.evidencePhotos?.length || task.completionPhotos?.length || 0;

  return (
    <Card className="hover:shadow-lg transition-shadow cursor-pointer" data-testid={`task-card-${task._id}`}>
      {/* Clickable summary row */}
      <CardContent className="p-6" onClick={onToggleExpand}>
        <div className="flex justify-between items-start">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <span className="text-2xl">{getServiceTypeIcon(task.serviceType || task.taskType)}</span>
              <h3 className="text-lg font-semibold">{task.title}</h3>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(task.status)}`}>
                {task.status}
              </span>
              {photoCount > 0 && (
                <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs flex items-center gap-1">
                  <Image size={12} /> {photoCount}
                </span>
              )}
            </div>

            <p className="text-gray-600 mb-3">{task.description}</p>

            <div className="grid md:grid-cols-2 gap-2 text-sm text-gray-500">
              <div className="flex items-center gap-1">
                <MapPin size={14} />
                {task.propertyTitle} - {task.propertyAddress}
              </div>
              <div className="flex items-center gap-1">
                <Calendar size={14} />
                {task.scheduledDate} {task.scheduledTime && `at ${task.scheduledTime}`}
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-2 ml-4 items-end" onClick={(e) => e.stopPropagation()}>
            {task.status === 'pending' && onAccept && (
              <Button size="sm" onClick={onAccept} className="bg-blue-600 hover:bg-blue-700">
                Accept
              </Button>
            )}
            {task.status === 'accepted' && onStart && (
              <Button size="sm" onClick={onStart} className="bg-purple-600 hover:bg-purple-700">
                <Play size={14} className="mr-1" /> Start
              </Button>
            )}
            {task.status === 'in_progress' && (
              <>
                <Button size="sm" variant="outline" onClick={onTakePhoto} disabled={sendingPhoto}>
                  {sendingPhoto ? (
                    <><Loader2 size={14} className="mr-1 animate-spin" /> Saving...</>
                  ) : (
                    <><Camera size={14} className="mr-1" /> Photos</>
                  )}
                </Button>
                {onComplete && (
                  <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={onComplete}>
                    <Check size={14} className="mr-1" /> Complete
                  </Button>
                )}
              </>
            )}
            <button className="text-gray-400 hover:text-gray-600 p-1">
              {expanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
            </button>
          </div>
        </div>
      </CardContent>

      {/* Expanded detail view */}
      {expanded && (
        <div className="border-t bg-gray-50 px-6 py-4 space-y-4">
          {/* Full job details */}
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-2">Job Details</h4>
              <div className="space-y-2 text-sm">
                <div className="flex items-start gap-2">
                  <span className="text-gray-500 font-medium w-24 flex-shrink-0">Type:</span>
                  <span className="capitalize">{(task.serviceType || task.taskType || '').replace(/_/g, ' ')}</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-gray-500 font-medium w-24 flex-shrink-0">Priority:</span>
                  <span className={`capitalize ${task.priority === 'urgent' ? 'text-red-600 font-bold' : task.priority === 'high' ? 'text-orange-600 font-semibold' : ''}`}>
                    {task.priority || 'normal'}
                  </span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-gray-500 font-medium w-24 flex-shrink-0">Scheduled:</span>
                  <span>{task.scheduledDate} {task.scheduledTime && `at ${task.scheduledTime}`}</span>
                </div>
                {(task.amount > 0 || task.estimatedCost > 0 || task.grossAmount > 0) && (
                  <div className="space-y-1">
                    <div className="flex items-start gap-2">
                      <span className="text-gray-500 font-medium w-24 flex-shrink-0">Gross Pay:</span>
                      <span className="font-semibold text-gray-900">£{(task.grossAmount || task.amount || task.estimatedCost || 0).toFixed(2)}</span>
                    </div>
                    {task.status === 'completed' && (
                      <>
                        <div className="flex items-start gap-2">
                          <span className="text-gray-500 font-medium w-24 flex-shrink-0">Platform Fee:</span>
                          <span className="text-red-600 text-sm">-£{(task.platformFee || ((task.grossAmount || task.amount || 0) * 0.05)).toFixed(2)} (5%)</span>
                        </div>
                        <div className="flex items-start gap-2">
                          <span className="text-gray-500 font-medium w-24 flex-shrink-0">Your Earnings:</span>
                          <span className="font-bold text-green-700">£{(task.netAmount || ((task.grossAmount || task.amount || 0) * 0.95)).toFixed(2)}</span>
                        </div>
                      </>
                    )}
                    {task.status !== 'completed' && (
                      <div className="flex items-start gap-2">
                        <span className="text-gray-500 font-medium w-24 flex-shrink-0">Est. Earnings:</span>
                        <span className="font-semibold text-green-700">£{((task.amount || task.estimatedCost || 0) * 0.95).toFixed(2)} <span className="text-xs text-gray-400">(after 5% fee)</span></span>
                      </div>
                    )}
                  </div>
                )}
                {task.completedAt && (
                  <div className="flex items-start gap-2">
                    <span className="text-gray-500 font-medium w-24 flex-shrink-0">Completed:</span>
                    <span>{new Date(task.completedAt).toLocaleString('en-GB')}</span>
                  </div>
                )}
              </div>
            </div>

            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-2">Property</h4>
              <div className="space-y-2 text-sm">
                <div className="flex items-start gap-2">
                  <Home size={14} className="mt-0.5 text-gray-400 flex-shrink-0" />
                  <span className="font-medium">{task.propertyTitle}</span>
                </div>
                <div className="flex items-start gap-2">
                  <MapPin size={14} className="mt-0.5 text-gray-400 flex-shrink-0" />
                  <span>{task.propertyAddress}</span>
                </div>
                {task.guestName && (
                  <div className="flex items-start gap-2">
                    <User size={14} className="mt-0.5 text-gray-400 flex-shrink-0" />
                    <span>Guest: {task.guestName}</span>
                  </div>
                )}
                {task.bookingId && (
                  <div className="flex items-start gap-2">
                    <FileText size={14} className="mt-0.5 text-gray-400 flex-shrink-0" />
                    <span className="text-xs text-gray-400">Booking: {task.bookingId}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Notes */}
          {task.notes && (
            <div className="p-3 bg-white rounded-lg border">
              <p className="text-sm text-gray-600"><strong>Host Notes:</strong> {task.notes}</p>
            </div>
          )}

          {task.completionNotes && (
            <div className="p-3 bg-green-50 rounded-lg border border-green-200">
              <p className="text-sm text-gray-700"><strong>Completion Notes:</strong> {task.completionNotes}</p>
            </div>
          )}

          {/* Evidence Photos — shown in all tabs when they exist */}
          {showPhotos && (task.evidencePhotos?.length > 0 || task.completionPhotos?.length > 0) && (
            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-1">
                <Shield size={14} className="text-green-600" /> Evidence Photos ({task.evidencePhotos?.length || task.completionPhotos?.length || 0})
              </h4>
              <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
                {(task.evidencePhotos || []).map((photo, idx) => (
                  <div
                    key={idx}
                    className="relative group cursor-pointer"
                    onClick={() => onViewPhoto && onViewPhoto(`${API_URL}${photo.url}`)}
                  >
                    <img
                      src={`${API_URL}${photo.url}`}
                      alt={`Evidence ${idx + 1}`}
                      className="w-full h-20 object-cover rounded-lg border"
                    />
                    <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-white text-[9px] px-1 py-0.5 rounded-b-lg truncate">
                      {photo.label} — {photo.timestampText || new Date(photo.capturedAt).toLocaleString('en-GB')}
                    </div>
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/20 rounded-lg">
                      <ZoomIn className="w-4 h-4 text-white drop-shadow-lg" />
                    </div>
                  </div>
                ))}
                {/* Fallback for legacy completionPhotos */}
                {!task.evidencePhotos?.length && (task.completionPhotos || []).map((url, idx) => (
                  <div key={idx} className="relative cursor-pointer" onClick={() => onViewPhoto && onViewPhoto(`${API_URL}${url}`)}>
                    <img src={`${API_URL}${url}`} alt={`Photo ${idx + 1}`} className="w-full h-20 object-cover rounded-lg border" />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* In-progress: take more photos prompt */}
          {task.status === 'in_progress' && onTakePhoto && (
            <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
              <Camera size={16} className="flex-shrink-0" />
              <p className="flex-1">Take live photos as evidence of your work. Photos are timestamped and cannot be edited.</p>
              <Button size="sm" variant="outline" className="border-blue-400 text-blue-700" onClick={onTakePhoto}>
                <Camera size={14} className="mr-1" /> Take Photo
              </Button>
            </div>
          )}

          {/* Message Host button */}
          {onMessageHost && (
            <Button
              variant="outline"
              size="sm"
              className="border-[#1F3A68] text-[#1F3A68]"
              onClick={onMessageHost}
            >
              <MessageSquare size={14} className="mr-1" /> Message Host
            </Button>
          )}
        </div>
      )}
    </Card>
  );
};

// Availability Editor Component
const AvailabilityEditor = ({ availability, onSave }) => {
  const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  const [schedule, setSchedule] = useState(
    days.map((day, idx) => {
      const existing = availability.find(a => a.dayOfWeek === idx);
      return {
        dayOfWeek: idx,
        dayName: day,
        available: existing?.available ?? true,
        startTime: existing?.startTime || '09:00',
        endTime: existing?.endTime || '17:00'
      };
    })
  );

  const updateDay = (idx, field, value) => {
    const newSchedule = [...schedule];
    newSchedule[idx] = { ...newSchedule[idx], [field]: value };
    setSchedule(newSchedule);
  };

  return (
    <div className="space-y-4">
      {schedule.map((day, idx) => (
        <div key={idx} className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
          <div className="w-28">
            <label className="flex items-center gap-2 cursor-pointer">
              <input 
                type="checkbox" 
                checked={day.available}
                onChange={(e) => updateDay(idx, 'available', e.target.checked)}
                className="w-4 h-4"
              />
              <span className="font-medium">{day.dayName}</span>
            </label>
          </div>
          
          {day.available && (
            <div className="flex items-center gap-2">
              <Input 
                type="time" 
                value={day.startTime}
                onChange={(e) => updateDay(idx, 'startTime', e.target.value)}
                className="w-32"
              />
              <span>to</span>
              <Input 
                type="time" 
                value={day.endTime}
                onChange={(e) => updateDay(idx, 'endTime', e.target.value)}
                className="w-32"
              />
            </div>
          )}
        </div>
      ))}
      
      <Button 
        onClick={() => onSave(schedule.filter(s => s.available))}
        className="bg-[#1F3A68] hover:bg-[#1F3A68]/90"
      >
        Save Availability
      </Button>
    </div>
  );
};

// Compliance Panel Component
const CompliancePanel = ({ compliance, complianceStatus, onUpload, uploading }) => {
  const pliRef = React.useRef(null);
  const dbsRef = React.useRef(null);
  const rtwRef = React.useRef(null);

  const statusColors = {
    compliant: 'bg-green-100 text-green-800 border-green-300',
    pending: 'bg-amber-100 text-amber-800 border-amber-300',
    blocked: 'bg-red-100 text-red-800 border-red-300',
    incomplete: 'bg-gray-100 text-gray-800 border-gray-300',
  };

  const docStatusColors = {
    verified: 'bg-green-100 text-green-700',
    pending: 'bg-amber-100 text-amber-700',
    rejected: 'bg-red-100 text-red-700',
    expired: 'bg-red-100 text-red-700',
    none: 'bg-gray-100 text-gray-500',
  };

  const pli = compliance?.publicLiabilityInsurance || {};
  const dbs = compliance?.dbsCheck || {};
  const rtw = compliance?.rightToWork || {};

  const formatDate = (d) => {
    if (!d) return '—';
    try {
      return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    } catch { return '—'; }
  };

  const documents = [
    {
      key: 'pli',
      title: 'Public Liability Insurance',
      description: 'Minimum coverage: \u00a31M-\u00a35M. Required to be in the dispatch pool.',
      data: pli,
      ref: pliRef,
      icon: '🛡️',
    },
    {
      key: 'dbs',
      title: 'DBS Check',
      description: 'Disclosure & Barring Service certificate for background check.',
      data: dbs,
      ref: dbsRef,
      icon: '🔒',
    },
    {
      key: 'right_to_work',
      title: 'Right to Work',
      description: 'Passport, visa, or settled/pre-settled status document.',
      data: rtw,
      ref: rtwRef,
      icon: '📋',
    },
  ];

  return (
    <div className="space-y-6">
      {/* Overall Status Banner */}
      <Card className={`border-2 ${statusColors[complianceStatus] || statusColors.incomplete}`}>
        <CardContent className="p-4 flex items-center gap-4">
          <Shield size={28} />
          <div className="flex-1">
            <h3 className="font-semibold text-lg">
              Compliance Status: <span className="capitalize">{complianceStatus || 'Incomplete'}</span>
            </h3>
            <p className="text-sm opacity-80">
              {complianceStatus === 'compliant' && 'All documents verified. You are eligible for task assignments.'}
              {complianceStatus === 'pending' && 'Documents submitted and awaiting admin verification.'}
              {complianceStatus === 'blocked' && 'Your insurance has expired. Upload a renewed certificate to continue receiving tasks.'}
              {complianceStatus === 'incomplete' && 'Upload your compliance documents below to get started.'}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Document Cards */}
      <div className="grid md:grid-cols-3 gap-6">
        {documents.map(doc => (
          <Card key={doc.key} className="overflow-hidden">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  <span className="text-xl">{doc.icon}</span>
                  {doc.title}
                </CardTitle>
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${docStatusColors[doc.data.status] || docStatusColors.none}`}>
                  {doc.data.status === 'none' ? 'Not Uploaded' : doc.data.status}
                </span>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-xs text-gray-500">{doc.description}</p>

              {/* Document Details */}
              {doc.data.documentUrl && (
                <div className="p-3 bg-gray-50 rounded-lg space-y-1">
                  {doc.data.policyNumber && (
                    <p className="text-xs"><span className="font-medium">Policy:</span> {doc.data.policyNumber}</p>
                  )}
                  {doc.data.certificateNumber && (
                    <p className="text-xs"><span className="font-medium">Certificate:</span> {doc.data.certificateNumber}</p>
                  )}
                  {doc.data.provider && (
                    <p className="text-xs"><span className="font-medium">Provider:</span> {doc.data.provider}</p>
                  )}
                  {doc.data.expiry && (
                    <p className="text-xs"><span className="font-medium">Expires:</span> {formatDate(doc.data.expiry)}</p>
                  )}
                  {doc.data.verifiedAt && (
                    <p className="text-xs"><span className="font-medium">Verified:</span> {formatDate(doc.data.verifiedAt)}</p>
                  )}
                  <a
                    href={doc.data.documentUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-blue-600 hover:underline flex items-center gap-1 mt-1"
                  >
                    <Eye size={12} /> View Document
                  </a>
                </div>
              )}

              {/* Upload Button */}
              <input
                type="file"
                ref={doc.ref}
                className="hidden"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={(e) => {
                  if (e.target.files?.[0]) onUpload(doc.key, e.target.files[0]);
                  e.target.value = '';
                }}
              />
              <Button
                variant={doc.data.documentUrl ? 'outline' : 'default'}
                size="sm"
                className={!doc.data.documentUrl ? 'bg-[#1F3A68] hover:bg-[#1F3A68]/90 w-full' : 'w-full'}
                disabled={uploading === doc.key}
                onClick={() => doc.ref.current?.click()}
              >
                {uploading === doc.key ? (
                  <span className="flex items-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current" />
                    Uploading...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Upload size={14} />
                    {doc.data.documentUrl ? 'Replace Document' : 'Upload Document'}
                  </span>
                )}
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Info Box */}
      <Card className="border-blue-200 bg-blue-50">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle size={20} className="text-blue-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-1">Important Information</p>
              <ul className="list-disc list-inside space-y-1 text-xs">
                <li>All documents are reviewed by an admin before approval.</li>
                <li>You will receive an email 30 days before your insurance expires.</li>
                <li>If your PLI expires, you will be automatically removed from the dispatch pool until renewed.</li>
                <li>Accepted file types: PDF, JPG, PNG (max 10 MB).</li>
                <li>By using this service, you agree to the{' '}
                  <a href="/terms#provider-terms" target="_blank" rel="noopener noreferrer" className="text-blue-700 underline">Service Provider Terms (Section 9)</a>
                  {' '}and{' '}
                  <a href="/terms#data-protection" target="_blank" rel="noopener noreferrer" className="text-blue-700 underline">Privacy Policy</a>.
                </li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ProviderDashboard;
